import axios from 'axios';
import { getToken } from '../utils/auth';
import { RegisterData } from '../types/User';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';
const AUTH_STORAGE_KEY = import.meta.env.VITE_AUTH_STORAGE_KEY || 'auth_token';

interface CustomRequestInit extends RequestInit {
  responseType?: 'blob' | 'json';
}

const api = axios.create({
  baseURL: API_URL
});

// Interceptor para renovar o token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Se o erro for 401 (Unauthorized) e não for uma tentativa de refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Tentar renovar o token
        const response = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${apiClient.getAuthToken()}`
          }
        });

        if (!response.ok) {
          throw new Error('Falha ao renovar token');
        }

        const data = await response.json();
        const { token } = data;

        // Atualizar o token no localStorage
        localStorage.setItem(AUTH_STORAGE_KEY, token);

        // Atualizar o header da requisição original
        originalRequest.headers['Authorization'] = `Bearer ${token}`;

        // Refazer a requisição original com o novo token
        return fetch(originalRequest.url, originalRequest);
      } catch (refreshError) {
        // Se não conseguir renovar, redirecionar para login
        localStorage.removeItem(AUTH_STORAGE_KEY);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const apiClient = {
  getAuthToken() {
    return localStorage.getItem('auth_token');
  },

  // Lista de rotas públicas que não precisam de token
  publicRoutes: [
    '/auth/login',
    '/auth/register',
    '/auth/verify',
    '/articles/public',
    '/articles/count'
  ],

  isPublicRoute(endpoint: string) {
    return this.publicRoutes.some(route => endpoint.includes(route));
  },

  async fetch(endpoint: string, options: CustomRequestInit = {}) {
    try {
      const token = this.getAuthToken();
      
      // Remover barra extra da URL se necessário
      const baseUrl = API_URL.endsWith('/') 
        ? API_URL.slice(0, -1) 
        : API_URL;
      
      // Garantir que o endpoint comece com / e não tenha /api
      const cleanEndpoint = endpoint
        .replace(/^\/api/, '') // Remove /api do início se existir
        .replace(/^\/+/, '/'); // Garante apenas uma barra no início
      
      const url = `${baseUrl}${cleanEndpoint}`;
      
      console.log('🔄 Requisição:', {
        url,
        method: options.method || 'GET',
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 20)}...` : null,
        headers: {
          ...options.headers,
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });

      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options.headers,
        },
      });

      console.log('📥 Resposta:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || `Erro na requisição: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📦 Dados:', data);
      return data;
    } catch (error) {
      console.error('❌ Erro na requisição:', error);
      throw error;
    }
  },

  // Artigos
  articles: {
    list: (page = 1, limit = 20) => 
      apiClient.fetch(`/articles?page=${page}&limit=${limit}`),
    
    get: (id: number) => 
      apiClient.fetch(`/articles/${id}`),
    
    update: async (id: number, data: any) => {
      const token = getToken();
      console.log('🔄 Atualizando artigo:', {
        id,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 10)}...` : null,
        API_URL: import.meta.env.VITE_API_URL,
        FULL_URL: `${import.meta.env.VITE_API_URL}/articles/${id}`
      });

      if (!token) {
        throw new Error('Usuário não autenticado');
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/articles/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro na requisição');
      }

      return response.json();
    },
    
    delete: (id: number) => {
      const token = apiClient.getAuthToken();
      if (!token) {
        throw new Error('Usuário não autenticado');
      }

      return apiClient.fetch(`/articles/${id}`, {
        method: 'DELETE'
      });
    }
  },

  async uploadPdf(formData: FormData) {
    try {
      console.log('📤 Enviando PDF:', {
        formData: {
          has_pdf: formData.has('pdf'),
          has_forceSave: formData.has('forceSave'),
          entries: Array.from(formData.entries()).map(([key, value]) => ({
            key,
            type: value instanceof File ? 'File' : typeof value
          }))
        }
      });

      const response = await fetch(`${API_URL}/article-processing/process-pdf`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Erro ao processar PDF: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('Erro ao fazer upload do PDF:', error);
      throw error;
    }
  },

  async uploadThumbnail(fileUuid: string, formData: FormData) {
    try {
      console.log('📤 Enviando thumbnail:', {
        fileUuid,
        formData: {
          has_thumbnail: formData.has('thumbnail')
        }
      });

      const response = await fetch(`${API_URL}/article-processing/process-pdf/temp-thumbnail/${fileUuid}`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        throw new Error(errorData.error || 'Erro ao fazer upload da thumbnail');
      }

      const data = await response.json();
      console.log('✅ Resposta do upload:', data);
      return data;
    } catch (error) {
      console.error('❌ Erro ao enviar thumbnail:', error);
      throw error;
    }
  },

  async checkPdfDoi(file: File) {
    try {
    const formData = new FormData();
    formData.append('pdf', file);

      console.log('📝 Verificando DOI do PDF...');

      const response = await fetch(`${API_URL}/article-processing/check-doi`, {
      method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      const data = await response.json();
      console.log('✅ Resposta da verificação:', data);

      return data;
    } catch (error) {
      console.error('❌ Erro ao verificar DOI:', error);
      throw error;
    }
  },

  async cancelProcessing(fileUuid: string) {
    try {
      const response = await fetch(`${API_URL}/article-processing/cancel/${fileUuid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao cancelar processamento');
      }

      const data = await response.json();
      console.log('✅ Processamento cancelado:', data);
      return data;
    } catch (error) {
      console.error('❌ Erro ao cancelar processamento:', error);
      throw error;
    }
  },

  async checkDOI(formData: FormData) {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/articles/check-doi`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Erro ao verificar DOI');
      }
      throw error;
    }
  },

  async extractAndCheckDOI(formData: FormData) {
    try {
      // Usar o endpoint existente de processamento de PDF
      const response = await fetch(`${API_URL}/article-processing/process-pdf`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao processar PDF');
      }

      const data = await response.json();
      const doi = data.article?.doi;

      if (!doi) {
        return { 
          isDuplicate: false, 
          doi: null,
          processedData: data // Incluir os dados processados mesmo sem DOI
        };
      }

      try {
        // Verificar se o DOI existe no banco
        const checkResponse = await fetch(`${API_URL}/articles/find-by-doi/${encodeURIComponent(doi)}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${this.getAuthToken()}`
          }
        });

        // Se o endpoint retornar 404, significa que o artigo não existe (não é duplicado)
        if (checkResponse.status === 404) {
          return {
            isDuplicate: false,
            doi,
            processedData: data
          };
        }

        if (!checkResponse.ok) {
          throw new Error('Erro ao verificar DOI no banco de dados');
        }

        const result = await checkResponse.json();
        return {
          isDuplicate: !!result.article,
          doi,
          existingArticle: result.article,
          processedData: data
        };
      } catch (error) {
        // Se houver erro na verificação do DOI, assumimos que não é duplicado
        console.warn('⚠️ Erro ao verificar DOI, assumindo não duplicado:', error);
        return {
          isDuplicate: false,
          doi,
          processedData: data
        };
      }
    } catch (error) {
      console.error('❌ Erro ao processar PDF:', error);
      throw error;
    }
  },

  async checkPDFDuplicate(file: File) {
    try {
      const formData = new FormData();
      formData.append('pdf', file);

      // Primeiro apenas extrair o DOI e verificar duplicidade
      const response = await fetch(`${API_URL}/articles/extract-doi`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao verificar DOI do PDF');
      }

      return await response.json();
    } catch (error) {
      console.error('❌ Erro ao verificar duplicidade:', error);
      throw error;
    }
  },

  auth: {
    async login(email: string, password: string) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        throw new Error('Erro na autenticação');
      }

      return response.json();
    },

    async register(data: RegisterData) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        // Tenta obter a mensagem de erro da API
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro no registro');
      }

      return response.json();
    },

    async forgotPassword(email: string) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error('Erro ao solicitar recuperação de senha');
      }
    },

    async resetPassword(token: string, password: string) {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      if (!response.ok) {
        throw new Error('Erro ao redefinir senha');
      }
    }
  },

  async downloadPDF(articleId: number) {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      throw new Error('Usuário não autenticado');
    }

    console.log('🔄 Iniciando download:', {
      articleId,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : null
    });

    const response = await fetch(`${import.meta.env.VITE_API_URL}/articles/download/${articleId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Erro ao baixar o PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `artigo-${articleId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
};

// Adicionar temporariamente
console.log('ENV:', {
  API_URL: import.meta.env.VITE_API_URL,
  NODE_ENV: import.meta.env.MODE,
  token: localStorage.getItem(AUTH_STORAGE_KEY)?.substring(0, 10) + '...'
}); 